import { useState } from "react";
import { preload } from "react-dom";
import {
  bakgrund,
  grammCentered,
  menuContact1Crop as menuContact1,
  menuLiveCrop as menuLive,
  menuWatchCrop as menuWatch,
  mouthFinal,
  myTurnCrop as myTurn,
  nyBakgrund,
  paradiseTalkBubble,
  paradiseTank,
  riverHogerCrop as riverHoger,
  riverHoger22Crop as riverHoger2,
  riverLogga,
  riverVansterCrop as riverVanster,
  riverVanster2Crop as riverVanster2,
  tapMeCrop as tapMe,
  tvShortV3 as tvShort,
} from "../assets";
import { useBackgroundAudioContext } from "../BackgroundAudio";
import { MuteToggle } from "../components/SiteChrome";
import { SocialIcons } from "../components/SocialIcons";
import { CITY_OF_THIRST_RELEASED } from "../release";
import { Link } from "../router";
import { useBackgroundWrapper } from "../useBackgroundWrapper";
import { CityPopup } from "./CityPopup";
import { DiscographyPopup } from "./DiscographyPopup";
import { VideoModal } from "./VideoModal";
import "./Home.css";

const FIGURE_RIGHT = [riverHoger, riverHoger2];
const FIGURE_LEFT = [riverVanster, riverVanster2];

/**
 * Which of the two skies is on screen, as the browser sees it.
 *
 * A portrait phone shows `bakgrund` — the same sky with the wordmark *not*
 * painted into it — stretched over the whole viewport, with `#river-logo` put
 * back on the canvas in the wordmark's place. Everything else shows
 * `ny_bakgrund`, wordmark and all, as the canvas itself. src/mobile/portrait.css
 * owns
 * that swap; these two are only so the right one gets the high-priority
 * preload. They must stay each other's exact complement.
 */
const SKY_MOBILE = "(max-width: 768px) and (orientation: portrait)";
const SKY_CANVAS = "(min-width: 769px), (orientation: landscape)";

export function Home() {
  // The sky and the TV are the two images that gate the first meaningful paint.
  preload(nyBakgrund, { as: "image", fetchPriority: "high", media: SKY_CANVAS });
  preload(bakgrund, { as: "image", fetchPriority: "high", media: SKY_MOBILE });
  preload(tvShort, { as: "image", fetchPriority: "high" });

  const { wrapperRef, imageRef, onLoad, wrapperClassName, wrapperStyle } =
    useBackgroundWrapper(nyBakgrund);
  // The loop itself is mounted above the router, so it survives navigation;
  // Home only borrows the ducking controls for its two players. The mute state
  // travels with `MuteToggle` instead. See src/BackgroundAudio.tsx.
  const { duck, unduck } = useBackgroundAudioContext();

  // The two figures each flip between two poses when tapped. "Tap me" points at
  // the left one; once she has been tapped, "my turn" points at the right one,
  // and stays gone for good after that.
  const [rightPose, setRightPose] = useState(0);
  const [leftPose, setLeftPose] = useState(0);
  const [tapMeVisible, setTapMeVisible] = useState(true);
  const [myTurnVisible, setMyTurnVisible] = useState(false);
  const [myTurnFinished, setMyTurnFinished] = useState(false);

  const [cityOpen, setCityOpen] = useState(false);
  const [discographyOpen, setDiscographyOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="page-home">
      {/* Outside the wrapper on purpose: this is the backdrop the canvas floats
          on, not part of the canvas, so it covers the viewport rather than the
          artwork and does not wait on the wrapper's fade. Hidden except on a
          portrait phone — see Home.css and src/mobile/portrait.css. */}
      <img id="home-sky" src={bakgrund} alt="" />
      <div id="bg-wrapper" className={wrapperClassName} style={wrapperStyle} ref={wrapperRef}>
        <img
          id="bg-image"
          className="bg-image"
          ref={imageRef}
          onLoad={onLoad}
          src={nyBakgrund}
          alt=""
        />
        {/* The wordmark #home-sky does not have painted into it, back on the
            pixels ny_bakgrund paints it on. Hidden wherever #bg-image is
            showing, which is the only other place it exists. */}
        <img id="river-logo" src={riverLogga} alt="River" />
        {/* The five `poster-row` wrappers are ticket 018's, and they carry no
            style of their own: every child inside them is `position: absolute`
            against .bg-wrapper, so on desktop each wrapper is a zero-height box
            that paints nothing and moves nothing. On a portrait phone
            src/mobile/portrait-home.css turns .bg-wrapper into a column and these into its
            rows — which is what lets the three menu words share a line, and the
            talk and think bubbles and the two hand-written labels stay pinned
            to the object they point at rather than becoming rows of their own,
            pointing at nothing. */}
        {/* Ticket 020 nests the two figure rows one level deeper, and that is
            the only DOM change on this page: a flex column cannot put two of
            its own children side by side, and on a portrait phone the two of
            them share the bottom row of the stack. On every other viewport
            this wrapper is another `poster-row` — a static, zero-height box
            that paints nothing and moves nothing, because every descendant is
            `position: absolute` and .bg-wrapper is still the only positioned
            ancestor, so the extra nesting changes no containing block. */}
        <div id="figure-pair" className="poster-row">
          <div id="figure-row-left" className="poster-row">
            <img
              id="left-side-figure"
              className="interactive"
              // Ticket 028: pressing a figure flips its pose and starts
              // nothing, so it is one of the presses that leaves the loop be.
              data-audio="keep"
              src={FIGURE_LEFT[leftPose]}
              alt=""
              onClick={() => {
                setLeftPose((p) => 1 - p);
                setTapMeVisible(false);
                if (!myTurnFinished) setMyTurnVisible(true);
              }}
            />
            {tapMeVisible && <img id="tap-me" src={tapMe} alt="" />}
          </div>
          <div id="figure-row-right" className="poster-row">
            <img
              id="left-figure"
              className="interactive"
              data-audio="keep"
              src={FIGURE_RIGHT[rightPose]}
              alt=""
              onClick={() => {
                setRightPose((p) => 1 - p);
                setMyTurnVisible(false);
                setMyTurnFinished(true);
              }}
            />
            {myTurnVisible && <img id="my-turn" src={myTurn} alt="" />}
          </div>
        </div>
        {/* Same treatment as the TV below: the lips stay on the poster, the way
            in does not. Before release the `<button>` is a `<div>` carrying the
            identical classes — every rule for this row is class-based, and
            `.mouth-hit` is what gives the object its width on a phone, so the
            swap moves nothing and leaves no dead button in the tab order. The
            bubble is the drawing that reads "Out now", so it goes; it is
            absolutely positioned, so its absence moves nothing either. */}
        <div id="mouth-row" className="poster-row">
          {CITY_OF_THIRST_RELEASED && (
            <button type="button" className="hitbox bubble-hit" onClick={() => setCityOpen(true)}>
              <img
                id="talk-bubble"
                className="interactive"
                src={paradiseTalkBubble}
                alt="Paradise - Out now"
              />
            </button>
          )}
          {CITY_OF_THIRST_RELEASED ? (
            <button type="button" className="hitbox mouth-hit" onClick={() => setCityOpen(true)}>
              <img
                id="mouth-wrap"
                className="interactive"
                src={mouthFinal}
                alt="Paradise - Out now"
              />
            </button>
          ) : (
            <div className="hitbox mouth-hit">
              <img id="mouth-wrap" src={mouthFinal} alt="" />
            </div>
          )}
        </div>
        {/* The TV stays on the poster before release — it is the landing page's
            signature object and says nothing about the single. What goes is the
            way in to the video: no click, and no `interactive`, which is the
            pointer cursor and the hover flimmer that promise one. The think
            bubble names the single, so it goes entirely; it is absolutely
            positioned inside this row, so its absence moves no other object. */}
        <div id="tv-row" className="poster-row">
          {CITY_OF_THIRST_RELEASED ? (
            <img
              id="tv-wrap"
              className="interactive"
              src={tvShort}
              alt=""
              onClick={() => setVideoOpen(true)}
            />
          ) : (
            <img id="tv-wrap" src={tvShort} alt="" />
          )}
          {CITY_OF_THIRST_RELEASED && (
            <img
              id="think-bubble"
              className="interactive"
              src={paradiseTank}
              alt="Paradise - Musikvideo"
              onClick={() => setVideoOpen(true)}
            />
          )}
        </div>
        <img
          id="gramm-wrap"
          className="interactive"
          src={grammCentered}
          alt=""
          onClick={() => setDiscographyOpen(true)}
        />
        {/* The three menu words are the named exception in ticket 028:
            moving between the site's own pages is not a departure from it. */}
        <div id="menu-row" className="poster-row" data-audio="keep">
          <Link to="/watch" className="menu-hit">
            <img id="menu-watch" className="menu-link interactive" src={menuWatch} alt="Watch" />
          </Link>
          <Link to="/live" className="menu-hit">
            <img id="menu-live" className="menu-link interactive" src={menuLive} alt="Live" />
          </Link>
          <Link to="/contact" className="menu-hit">
            <img
              id="menu-contact"
              className="menu-link interactive"
              src={menuContact1}
              alt="Contact"
            />
          </Link>
        </div>
        <SocialIcons />
        {/* Same place in the DOM it was rendered from when it was Home's own
            `<img>`, so the canvas it is positioned against is unchanged. The
            component is shared with the three subpages now — ticket 024. */}
        <MuteToggle />
      </div>

      {/* Unmounted rather than left closed: it holds the video still and the
          YouTube id, and nothing can open it anyway. */}
      {CITY_OF_THIRST_RELEASED && (
        <VideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          duck={duck}
          unduck={unduck}
        />
      )}
      {/* Not just unreachable — unmounted. The component `preload()`s its own
          backdrop during render, so leaving it mounted with open={false} would
          put the unreleased artwork on the wire where anyone can read it off the
          network tab. */}
      {CITY_OF_THIRST_RELEASED && (
        <CityPopup open={cityOpen} onClose={() => setCityOpen(false)} duck={duck} unduck={unduck} />
      )}
      <DiscographyPopup open={discographyOpen} onClose={() => setDiscographyOpen(false)} />
    </div>
  );
}
